import { useMemo } from 'react';
import {
  Patient,
  Practitioner,
  ServiceRequest,
  Specimen,
} from '@medplum/fhirtypes';
import { ReportBindingData } from '../types/template.types';
import {
  createReportBindingData,
  updateReportStatus,
} from '../services/data-binding.service';

export interface UseReportDataBindingProps {
  serviceRequest?: ServiceRequest | null;
  patient?: Patient;
  requester?: Practitioner;
  specimens?: Specimen[];
  reportId?: string;
  reportStatus?: 'draft' | 'preliminary' | 'final';
}

export interface UseReportDataBindingReturn {
  /** Prepared binding data for template population */
  bindingData: ReportBindingData | undefined;
  /** Whether all required data is available */
  isDataReady: boolean;
  /** Missing data fields for debugging */
  missingFields: string[];
}

/**
 * Hook to prepare report binding data from order details
 * Combines patient, order, and specimen data into format expected by templates
 * @param root0 - Hook configuration options
 * @param root0.serviceRequest - FHIR ServiceRequest resource
 * @param root0.patient - FHIR Patient resource
 * @param root0.requester - FHIR Practitioner who requested the order
 * @param root0.specimens - Array of FHIR Specimen resources
 * @param root0.reportId - Optional existing report ID
 * @param root0.reportStatus - Report status (draft, preliminary, final)
 * @returns Binding data and readiness state
 */
export function useReportDataBinding({
  serviceRequest,
  patient,
  requester,
  specimens,
  reportId,
  reportStatus = 'draft',
}: UseReportDataBindingProps): UseReportDataBindingReturn {
  const bindingData = useMemo(() => {
    if (!serviceRequest || !patient) {
      return undefined;
    }

    const data = createReportBindingData(
      serviceRequest,
      patient,
      requester,
      specimens
    );

    // Update with report info if available
    if (reportId || reportStatus !== 'draft') {
      return updateReportStatus(data, reportStatus, reportId);
    }

    return data;
  }, [serviceRequest, patient, requester, specimens, reportId, reportStatus]);

  const missingFields = useMemo(() => {
    const missing: string[] = [];

    if (!serviceRequest) {
      missing.push('serviceRequest');
    }
    if (!patient) {
      missing.push('patient');
    }
    if (!requester) {
      missing.push('requester');
    }
    if (!specimens || specimens.length === 0) {
      missing.push('specimens');
    }

    return missing;
  }, [serviceRequest, patient, requester, specimens]);

  const isDataReady = useMemo(() => {
    // Minimum required: serviceRequest and patient
    return !!serviceRequest && !!patient;
  }, [serviceRequest, patient]);

  return {
    bindingData,
    isDataReady,
    missingFields,
  };
}
